import { useId } from 'react';
import { createPortal } from 'react-dom';
import { QuantityStepper } from './QuantityStepper';
import { RipButton } from './RipButton';
import { formatPrice } from '../../../../modules/currency';

export interface BottomActionProps {
  quantity: number;
  onQuantityChange: (next: number) => void;
  onBuy: () => void;
  /** Per-unit price (number like 25 or string like "25.0"). */
  unitPrice: number | string;
  /** Minimum / maximum quantity the user may pick. */
  minQty?: number;
  maxQty?: number;
  /** Pack not purchasable (e.g. "Restocking") — the rail stays, locked, and
   *  the RIP button reads this label. */
  unavailableLabel?: string;
}

interface BarProps {
  quantity: number;
  minQty: number;
  maxQty: number;
  onQuantityChange: (next: number) => void;
  onBuy: () => void;
  priceLabel: string;
  disabled: boolean;
  /** Nothing to buy — `priceLabel` carries the "Sold out" / "Restocking"
   *  copy, the RIP button drops its quantity line and the stepper locks. */
  locked: boolean;
}

// Shell background is a 2-stop linear gradient. The direction lives in its
// own CSS var so the operator can flip horizontal/vertical from the
// VariantPicker without our React code observing the var. When both stops
// hold the same colour, the gradient renders as a solid fill.
const SHELL_BG =
  'linear-gradient(' +
  'var(--co-BottomActionShell-gradientDirection, to bottom), ' +
  'var(--co-BottomActionShell-gradientFrom, #ffffff), ' +
  'var(--co-BottomActionShell-gradientTo, #ffffff))';
const SHELL_BORDER = 'var(--co-BottomActionShell-border, rgba(171,174,179,0.24))';

// Host-app integration: embedded in an app that draws its own bottom
// navigation, the mobile rail ends up underneath it. Two Design
// Constructor knobs let the operator clear that UI without a code change:
//
//   marginBottom  — lifts the whole rail off the viewport bottom, leaving a
//                   transparent gap (host app's own background shows).
//   paddingBottom — keeps the rail glued to the bottom edge but extends the
//                   shell fill below the controls, so the host UI overlaps
//                   solid shell instead of the RIP button.
// `max(0px, …)` clamps a negative operator value to zero: the knob is a
// number input with spinner arrows, and a negative `bottom` would push the
// whole rail below the viewport (bar gone, no way to tell why).
const SHELL_MARGIN_BOTTOM = 'max(0px, var(--co-BottomActionShell-marginBottom, 0px))';
const SHELL_PADDING_BOTTOM = 'max(0px, var(--co-BottomActionShell-paddingBottom, 0px))';

// Mobile rail shadow. `drop-shadow()` and not `box-shadow` because the rail's
// silhouette is composed of two clip-path shapes — a box-shadow would trace
// the wrapper's rectangle instead, and a box-shadow on the shapes themselves
// is clipped away with them. Knobs so a host app whose own bottom navigation
// sits right under the rail can soften or remove it (colour `transparent`)
// without a code change.
const SHELL_SHADOW =
  'drop-shadow(0 ' +
  'var(--co-BottomActionShell-shadowOffsetY, -6px) ' +
  'var(--co-BottomActionShell-shadowBlur, 20px) ' +
  'var(--co-BottomActionShell-shadowColor, rgba(0,0,0,0.18)))';

// Operator-placed RIP disc. The disc sits in a full-width row so `align` can
// pin it right / centre / left on any bar width; the offsets nudge it from
// there. `translate`, not `transform`, so the desktop `scale-90` stays intact.
const RIP_ALIGN = 'var(--co-RipButton-align, flex-end)';
const RIP_OFFSET = 'var(--co-RipButton-offsetX, 0px) var(--co-RipButton-offsetY, 0px)';

/** Height of the flat fill band below the controls. It carries the host's bottom
 *  inset, so the shell paints BEHIND the host's own navigation rather than
 *  stopping above it — the rail used to be lifted by `--page-safe-bottom`, which
 *  left a strip of page background between the white shell and the host's tab
 *  bar, reported as an unfinished edge. Controls stay above the inset either
 *  way; only the fill grows downward. Plus whatever extra the operator dialled
 *  in. */
const SHELL_FILL_BELOW = `calc(var(--page-safe-bottom, 0px) + ${SHELL_PADDING_BOTTOM})`;

/** In-flow spacer height reserving room for the portal'd mobile rail:
 *  the rail's own 156px (mirrors `h-[156px]` in MobileBar) plus whatever
 *  bottom margin / padding the operator dialled in. The host inset is NOT
 *  added here, for two reasons that happen to agree: `#root` already carries
 *  it as `padding-bottom`, and the inset now extends the rail DOWNWARD into
 *  the strip rather than lifting it — so the rail's top edge, which is all
 *  this spacer has to clear, does not move. Consumed by PackLayout. */
export const MOBILE_RAIL_SPACER_HEIGHT =
  `calc(156px + ${SHELL_MARGIN_BOTTOM} + ${SHELL_PADDING_BOTTOM})`;

/**
 * Mobile rail — edge-to-edge, fixed to viewport bottom.
 *
 * Shape = two overlapping shell divs, each shaped by its own `clip-path`:
 *
 *   1. Wave (left/centre): a full-size shell div clipped by a quadratic
 *      Bézier curve that SAGS in the middle (edges at y≈54.7%, centre
 *      drops to y≈61.7%, then rises back). Uses SVG `<clipPath>` because
 *      CSS `ellipse()` can only bulge outward — this curve is concave.
 *
 *   2. Plateau (right): a rectangle sized 69.6%×105.97% clipped by
 *      `inset(0 round 110px 0 0 0)` — rounded top-left corner only,
 *      matching Figma's `rounded-tl-[110px]`. Gradient shell-fill → #F4F4F4.
 *
 * Shell colour is driven by `--co-BottomActionShell-fill` (Design
 * Constructor knob), defaulting to white.
 */
function MobileBar({
  quantity,
  minQty,
  maxQty,
  onQuantityChange,
  onBuy,
  priceLabel,
  disabled,
  locked,
}: BarProps) {
  // useId so two mounted copies (mobile+desktop wrappers) don't collide.
  const clipId = `rail-wave-${useId().replace(/:/g, '')}`;

  // Render into <body> so the rail is guaranteed to be positioned against
  // the viewport (no ancestor `transform` / `filter` / `contain` can hijack
  // its containing block).
  //
  // The OUTER fixed div is its own `@container`: a `fixed left-0 right-0`
  // element spans the viewport EXCLUDING a classic scrollbar — the exact
  // width the pack grid's `@[800px]` container query measures. The INNER
  // div hides on `@[800px]`, so the mobile bar disappears exactly when the
  // desktop bar appears. A `min-[800px]:hidden` viewport media query here
  // would count the scrollbar in and leave a scrollbar-wide window-width
  // band (~800–816px) with NO action bar at all.
  //
  // `z-[45]`, not the `z-30` of ordinary page chrome: the shell paints THROUGH
  // the host's bottom inset now, and Backdrop's bottom inset cover (z-40) fills
  // that same strip with the page background — from z-30 the cover painted over
  // the shell's fill band and the seam came straight back. Still below the
  // dialog layer (z-50). The shuffle reel's z-40 is not a clash either: the pack
  // view, and with it this portal, is unmounted while a reveal is on screen.
  return createPortal(
    <div
      className="@container fixed left-0 right-0 z-[45]"
      style={{ bottom: SHELL_MARGIN_BOTTOM }}
    >
      <div
        data-co-component="BottomActionShell"
        className="relative @[800px]:hidden"
        style={{ filter: SHELL_SHADOW }}
      >
        {/* 176px (was 201): the wave/plateau clip paths are objectBoundingBox-
            relative, so shrinking the shell compresses the whole rail — the
            visible band drops from ~91px to ~80px at the edges. */}
        <div className="relative mx-auto h-[156px] w-full max-w-[1400px] overflow-hidden">
          {/* SVG defs — invisible, just hosts the wave clipPath */}
          <svg
            aria-hidden
            width="0"
            height="0"
            className="absolute"
            style={{ position: 'absolute' }}
          >
            <defs>
              <clipPath id={clipId} clipPathUnits="objectBoundingBox">
                {/* Quadratic Bézier sag: edges y=0.547, centre sags to y=0.617 */}
                <path d="M 0 0.547 Q 0.5 0.687 1 0.547 L 1 1 L 0 1 Z" />
              </clipPath>
            </defs>
          </svg>

          {/* 1. Wave — clipped by SVG <clipPath> (concave curve) */}
          <div
            aria-hidden
            className="absolute inset-0"
            style={{ clipPath: `url(#${clipId})`, background: SHELL_BG }}
          />

          {/* 2. Plateau — clipped by CSS inset with TL rounded corner */}
          <div
            aria-hidden
            className="absolute"
            style={{
              left: '57.07%',
              top: '31.34%',
              width: '69.6%',
              height: '105.97%',
              clipPath: 'inset(0 round 110px 0 0 0)',
              background: SHELL_BG,
            }}
          />

        </div>

        {/* Bottom padding band — flat shell fill continuing below the rail.
            The wave clip fills the rail edge-to-edge at its bottom edge, so
            this joins seamlessly and just makes the shell taller. 0px by
            default, i.e. no extra element height. */}
        <div
          aria-hidden
          className="mx-auto w-full max-w-[1400px]"
          style={{ height: SHELL_FILL_BELOW, background: SHELL_BG }}
        />

        {/* Controls sit OUTSIDE the shape container and AFTER the fill band,
            not inside the rail box with the wave and the plateau.
            `overflow-hidden` up there exists to trim those two clip-path
            pieces (the plateau is 106% tall) and it was trimming the RIP
            disc's drop shadow with them: the shadow reaches 78px below the
            disc (`0 14px 64px`), the box ends 6px below it, and the opaque
            fill band then painted over whatever survived. The host app
            reported the result as a hard line above its tab bar, and the
            shell's own `shadow*` knobs could not touch it because the shadow
            belongs to the button, not the rail.
            Anchored to the band's top instead of the container's bottom, so
            the controls do not move a pixel while their shadows now fall
            across the band. */}
        <div
          className="absolute left-4"
          style={{ bottom: `calc(${SHELL_FILL_BELOW} + 26px)` }}
        >
          <QuantityStepper
            value={quantity}
            min={minQty}
            max={maxQty}
            onChange={onQuantityChange}
            size="md"
            disabled={locked}
          />
        </div>

        {/* The row spans the bar, so it lets taps through to the stepper. */}
        <div
          className="pointer-events-none absolute inset-x-[5px] flex"
          style={{ bottom: `calc(${SHELL_FILL_BELOW} + 6px)`, justifyContent: RIP_ALIGN }}
        >
          <div className="pointer-events-auto" style={{ translate: RIP_OFFSET }}>
            <RipButton
              onClick={onBuy}
              count={quantity}
              priceLabel={priceLabel}
              size="md"
              disabled={disabled}
              locked={locked}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Desktop rail — CSS-only, static, sits inside the sidebar column.
 * Uses the sidebar's own width; RIP button extends below the rail.
 * Shell fill/border are driven by Design Constructor CSS vars with
 * sensible defaults.
 */
function DesktopBar({
  quantity,
  minQty,
  maxQty,
  onQuantityChange,
  onBuy,
  priceLabel,
  disabled,
  locked,
}: BarProps) {
  return (
    <div
      data-co-component="BottomActionShell"
      className="hidden @[800px]:block relative min-h-[220px] w-full"
    >
      <div
        aria-hidden
        className="
          absolute inset-x-0 top-0 h-[127px]
          rounded-tl-[28px] rounded-tr-[28px]
          rounded-bl-[68px] rounded-br-[28px]
          [box-shadow:0_64px_34px_0_rgba(0,0,0,0.05)]
        "
        style={{
          background: SHELL_BG,
          border: `var(--co-BottomActionShell-borderWidth, 2px) solid ${SHELL_BORDER}`,
        }}
      />
      <div className="absolute left-[14px] top-[14px]">
        <QuantityStepper
          value={quantity}
          min={minQty}
          max={maxQty}
          onChange={onQuantityChange}
          size="lg"
          disabled={locked}
        />
      </div>
      {/* 800–1200px the sidebar is only 360px, so the RIP disc and the
          quantity stepper overlap. Shrink the disc (and its text) 10% toward
          the top-right corner so it pulls clear of the stepper; ≥1200px the
          sidebar widens to 400px and it sits at full size again. */}
      <div
        className="pointer-events-none absolute inset-x-[12px] top-[10px] flex"
        style={{ justifyContent: RIP_ALIGN }}
      >
        <div
          className="pointer-events-auto origin-top-right @max-[1200px]:scale-90"
          style={{ translate: RIP_OFFSET }}
        >
          <RipButton
            onClick={onBuy}
            count={quantity}
            priceLabel={priceLabel}
            size="lg"
            disabled={disabled}
            locked={locked}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Pack-page bottom action.
 *
 *   < 800px (mobile) — `fixed bottom-0`, edge-to-edge, SVG rail.
 *   ≥ 800px (desktop) — `static`, CSS-only rail inside sidebar.
 *
 * Both bars hide/show via `@[800px]` container queries measuring the SAME
 * layout width (the mobile bar's body portal carries its own `@container`
 * on the fixed wrapper — see MobileBar), so exactly one bar is visible at
 * any width. A viewport media query here would drift from the container
 * by the scrollbar width.
 */
export function BottomAction({
  quantity,
  onQuantityChange,
  onBuy,
  unitPrice,
  minQty = 1,
  maxQty = 99,
  unavailableLabel,
}: BottomActionProps) {
  const unitAmount = Number(unitPrice) || 0;
  const totalAmount = unitAmount * Math.max(quantity, 1);
  const lockedLabel = unavailableLabel ?? (maxQty <= 0 ? 'Sold out' : null);
  const priceLabel = lockedLabel ?? formatPrice(totalAmount);

  const shared: BarProps = {
    quantity,
    minQty,
    maxQty,
    onQuantityChange,
    onBuy,
    priceLabel,
    disabled: lockedLabel !== null,
    locked: lockedLabel !== null,
  };

  return (
    <>
      <MobileBar {...shared} />
      <DesktopBar {...shared} />
    </>
  );
}
